import { Badge, Group, Stack, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelActivityType, TravelDayBalanceSummaryYear } from '../model/TravelRequestTypes';
import { travelDayBalanceColor } from './TravelDayBalanceCommon';

interface TravelDayBalancePool {
  used: number;
  requested: number;
  cap: number;
}

export interface TravelDayBalanceSectionYear extends TravelDayBalanceSummaryYear {
  professionalDevelopment?: TravelDayBalancePool;
}

export interface TravelDayBalanceSectionProps {
  activityType: TravelActivityType;
  leaveDaysExceeded: boolean;
  dayCapExceeded: boolean;
  dayBalance: { consumes: boolean; years: TravelDayBalanceSectionYear[] } | null;
  children?: ReactNode;
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <Text
      size="xs"
      style={{ whiteSpace: 'nowrap' }}
    >
      <Text
        span
        inherit
        c="dimmed"
      >
        {label}
      </Text>{' '}
      <Text
        span
        inherit
        fw={600}
      >
        {value}
      </Text>
    </Text>
  );
}

function Dot() {
  return (
    <Text
      size="xs"
      c="dimmed"
      aria-hidden
    >
      ·
    </Text>
  );
}

interface PoolRowProps extends TravelDayBalancePool {
  prefix: string | null;
  exceeded: boolean;
}

function PoolRow({ prefix, used, requested, cap, exceeded }: PoolRowProps) {
  const { t } = useTranslation();
  const total = used + requested;
  const over = total - cap;

  let tag = t('travel.requests.dayBalance.section.left', { remaining: cap - total });
  if (over > 0) tag = t('travel.requests.dayBalance.section.over', { over });
  else if (exceeded) tag = t('travel.requests.dayBalance.dayCapExceeded');
  else if (over === 0) tag = t('travel.requests.dayBalance.section.atCap');

  return (
    <Group
      gap={6}
      wrap="wrap"
      align="center"
    >
      {prefix != null && (
        <Text
          size="xs"
          fw={500}
        >
          {prefix}
        </Text>
      )}
      <Item
        label={t('travel.requests.dayBalance.section.usedBefore')}
        value={String(used)}
      />
      <Dot />
      <Item
        label={t('travel.requests.form.dayBalance.thisRequest')}
        value={String(requested)}
      />
      <Dot />
      <Item
        label={t('travel.requests.dayBalance.section.total')}
        value={`${total} / ${cap}`}
      />
      <Badge
        size="xs"
        variant="light"
        tt="none"
        color={exceeded ? 'red' : travelDayBalanceColor(total, cap)}
      >
        {tag}
      </Badge>
    </Group>
  );
}

interface LeaveRowProps {
  prefix: string | null;
  label: string;
  otherRequests: number;
  thisRequest: number;
}

function LeaveRow({ prefix, label, otherRequests, thisRequest }: LeaveRowProps) {
  const { t } = useTranslation();

  return (
    <Group
      gap={6}
      wrap="wrap"
      align="center"
    >
      {prefix != null && (
        <Text
          size="xs"
          fw={500}
        >
          {prefix}
        </Text>
      )}
      <Text
        size="xs"
        fw={500}
      >
        {label}
      </Text>
      <Item
        label={t('travel.requests.form.dayBalance.thisRequest')}
        value={String(thisRequest)}
      />
      <Dot />
      <Item
        label={t('travel.requests.dayBalance.section.yearTotal')}
        value={String(otherRequests + thisRequest)}
      />
    </Group>
  );
}

interface LeaveRowsProps {
  prefix: string | null;
  year: TravelDayBalanceSectionYear;
}

function LeaveRows({ prefix, year }: LeaveRowsProps) {
  const { t } = useTranslation();
  const { vacationDays, unpaidDays, thisRequestVacationDays, thisRequestUnpaidDays } = year;

  if (vacationDays + thisRequestVacationDays === 0 && unpaidDays + thisRequestUnpaidDays === 0) return null;

  return (
    <>
      <LeaveRow
        prefix={prefix}
        label={t('travel.requests.dayBalance.section.vacationDays')}
        otherRequests={vacationDays}
        thisRequest={thisRequestVacationDays}
      />
      <LeaveRow
        prefix={prefix}
        label={t('travel.requests.dayBalance.section.unpaidDays')}
        otherRequests={unpaidDays}
        thisRequest={thisRequestUnpaidDays}
      />
    </>
  );
}

function FlagLine({ text }: { text: string }) {
  return (
    <Text
      size="xs"
      c="red"
      fw={500}
    >
      {text}
    </Text>
  );
}

export function TravelDayBalanceSection({
  activityType,
  leaveDaysExceeded,
  dayCapExceeded,
  dayBalance,
  children,
}: TravelDayBalanceSectionProps) {
  const { t } = useTranslation();

  if (dayBalance == null && !leaveDaysExceeded && !dayCapExceeded) return null;

  const multiYear = (dayBalance?.years.length ?? 0) > 1;
  const professionalDevelopmentLabel = t('travel.requests.dayBalance.detail.professionalDevelopment');

  return (
    <Stack
      gap={4}
      pt="xs"
      role="group"
      aria-label={t('travel.requests.dayBalance.section.title')}
      style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
    >
      <Text
        size="xs"
        fw={500}
        c="dimmed"
      >
        {t('travel.requests.dayBalance.section.title')}
      </Text>
      {dayBalance != null && !dayBalance.consumes && (
        <Text
          size="xs"
          c="dimmed"
        >
          {t(
            activityType === 'ERASMUS'
              ? 'travel.requests.dayBalance.notConsumingErasmus'
              : 'travel.requests.dayBalance.notConsumingOwnPrograms',
          )}
        </Text>
      )}
      {dayBalance?.consumes === true &&
        dayBalance.years.map(balanceYear => {
          const { year, withPaperUsed, thisRequestPaidDays, withPaperCap, exceeded, professionalDevelopment } =
            balanceYear;

          return (
            <Stack
              key={year}
              gap={4}
            >
              <PoolRow
                prefix={multiYear ? `${year}:` : null}
                used={withPaperUsed}
                requested={thisRequestPaidDays}
                cap={withPaperCap}
                exceeded={exceeded}
              />
              {professionalDevelopment != null && (
                <PoolRow
                  prefix={multiYear ? `${professionalDevelopmentLabel} ${year}:` : `${professionalDevelopmentLabel}:`}
                  exceeded={false}
                  {...professionalDevelopment}
                />
              )}
              <LeaveRows
                prefix={multiYear ? `${year}:` : null}
                year={balanceYear}
              />
            </Stack>
          );
        })}
      {leaveDaysExceeded && <FlagLine text={t('travel.requests.dayBalance.leaveDaysExceeded')} />}
      {dayCapExceeded && <FlagLine text={t('travel.requests.dayBalance.dayCapExceeded')} />}
      {children}
    </Stack>
  );
}
